import React, { useState, useEffect } from "react";
import API from "../Services/api";
import UserProfile from "./Dashboard/UserProfile";
import DashboardSummary from "./Dashboard/DashboardSummary";
import Dashboard from "./Dashboard/Dashboard";
import FeedbackWidget from "./Dashboard/FeedbackWidget";
import RecyclingHistory from "./Dashboard/RecyclingHistory";
import Header from "./Header";
import Footer from "./Footer";
import OnboardingGuide from "./Dashboard/OnboardingGuide";

function Profile() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    let timeOutId;
    const fetchProfile = async () => {
      try {
        const response = await API.get("/users/profile");
        const userData = response.data;
        setUser(userData);

        const seen = localStorage.getItem("esave_onboarding_seen");
        const isNewUser = !userData?.recyclingHistory?.length;
        if (!seen && isNewUser) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error("Error fetching profile", error);
        clearTimeout(timeOutId);
        setShowError(true);
      } finally {
        clearTimeout(timeOutId);
        setIsLoading(false);
      }
    };
    fetchProfile();
    timeOutId = setTimeout(() => {
      setIsLoading(false);
      setShowError(true);
    }, 15000);
    return () => clearTimeout(timeOutId);
  }, []);

  const hasRecyclingHistory = user?.recyclingHistory?.length > 0;

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F8FAFB]">
      {isLoading ? (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-white bg-opacity-75">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#29752B]"></div>
        </div>
      ) : showError ? (
        <div className="fixed inset-0 flex flex-col items-center justify-center z-50 bg-[#F8FAFB]">
          <img src="/images/internet-con.png" alt="No internet" className="h-24 w-24 mb-4" />
          <h3 className="text-xl font-bold text-[#172A3A] mb-2">Oops! Something went wrong.</h3>
          <p className="text-[#414042] text-center">
            Please check your internet connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#29752B] text-white px-6 py-3 rounded-lg mt-6 hover:bg-[#18421B]"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {showOnboarding && (
            <OnboardingGuide onClose={() => setShowOnboarding(false)} />
          )}

          <Header isDashboard={true} user={user} isAuthenticated={true} />

          <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow">
            <div className="flex flex-col lg:flex-row lg:space-x-4">
              {/* Left side — User Profile Card */}
              <div className="w-full lg:w-1/3 mb-8 lg:mb-0 z-[3]">
                <div className="h-full w-full flex flex-col">
                  <UserProfile user={user} />
                </div>
              </div>

              {/* Right side — Summary Panels */}
              <div className="w-full lg:w-2/3 bg-[#EBEDF0] p-6 md:p-4 rounded-3xl shadow-sm" data-aos="fade-in" data-aos-delay="200">
                <div className="h-full flex flex-col">
                  <DashboardSummary user={user} />
                </div>
              </div>
            </div>
          </main>

          {/* How Rewards Work — always visible to logged-in users */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-2">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 px-6 py-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-base font-bold text-[#172A3A]">How Rewards Work</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Earn eco points every time you recycle — then spend them on real rewards.</p>
                </div>
                <a
                  href="/rewards"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#004d3f] px-4 py-2 rounded-full hover:bg-[#003d32] transition whitespace-nowrap self-start sm:self-auto"
                >
                  <i className="fas fa-gift"></i>
                  Browse Rewards
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                {[
                  { step: '1', icon: 'fas fa-map-marker-alt', color: 'bg-blue-50 text-blue-600', title: 'Find a Centre', body: 'Use the map to locate your nearest e-waste collection point.' },
                  { step: '2', icon: 'fas fa-recycle', color: 'bg-green-50 text-[#29752B]', title: 'Drop off &amp; Recycle', body: 'Submit a recycling request and drop off your old electronics.' },
                  { step: '3', icon: 'fas fa-coins', color: 'bg-yellow-50 text-yellow-600', title: 'Earn Eco Points', body: 'Points are added once your drop-off is confirmed by staff.' },
                  { step: '4', icon: 'fas fa-gift', color: 'bg-purple-50 text-purple-600', title: 'Redeem Rewards', body: 'Spend points on discounts, vouchers, gift cards &amp; experiences.' },
                ].map(({ step, icon, color, title, body }) => (
                  <div key={step} className="flex items-start gap-3">
                    <div className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center ${color}`}>
                      <i className={`${icon} text-sm`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#172A3A]" dangerouslySetInnerHTML={{ __html: title }} />
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug" dangerouslySetInnerHTML={{ __html: body }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:space-x-8">
              {hasRecyclingHistory ? (
                <div className="w-full lg:w-3/4 mx-auto">
                  <div className="mb-8">
                    <Dashboard user={user} />
                  </div>
                  <RecyclingHistory user={user} />
                </div>
              ) : (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                  <div className="w-full mx-auto lg:w-3/4">
                    <div className="bg-white rounded-3xl shadow-md p-10 text-center">
                      <div className="text-5xl mb-4">♻️</div>
                      <p className="text-lg font-semibold text-[#172A3A] mb-2" data-aos="fade-up" data-aos-delay="0">
                        No recycling history yet
                      </p>
                      <p className="text-sm text-gray-500 mb-6" data-aos="fade-up" data-aos-delay="200">
                        Find a collection centre near you and submit your first recycling request to get started.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center" data-aos="fade-up" data-aos-delay="400">
                        <button
                          className="flex items-center justify-center bg-[#29752B] text-white px-8 py-2.5 rounded-full hover:bg-[#004d3f] transition"
                          onClick={() => { window.location.href = "/map"; }}
                        >
                          <img src="/images/globe.png" alt="Map" className="w-5 h-5 mr-2" />
                          Find a Centre
                        </button>
                        <button
                          className="flex items-center justify-center bg-white border border-gray-300 text-[#344054] px-8 py-2.5 rounded-full hover:bg-gray-50 transition"
                          onClick={() => setShowOnboarding(true)}
                        >
                          ❓ How it works
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <FeedbackWidget />
          <Footer />
        </>
      )}
    </div>
  );
}

export default Profile;
